// src/context/ModalContext.js

import React, { createContext, useState, useContext, useCallback, useEffect, cloneElement } from 'react';
import { TransitionGroup, CSSTransition } from 'react-transition-group';

const ModalContext = createContext();

export const useModal = () => useContext(ModalContext);

export const ModalProvider = ({ children }) => {
  const [modalStack, setModalStack] = useState([]);

  const showModal = useCallback((component) => {
    const id = Math.random().toString(36).substring(2, 9);
    setModalStack((stack) => [...stack, { id, component }]);
  }, []);

  const hideModal = useCallback(() => {
    setModalStack((stack) => stack.slice(0, -1));
  }, []);

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && modalStack.length > 0) {
        hideModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [modalStack, hideModal]);

  useEffect(() => {
    document.body.style.overflow = modalStack.length > 0 ? 'hidden' : 'unset';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [modalStack.length]);

  const value = {
    showModal,
    hideModal,
    modalStack,
  };

  return (
    <ModalContext.Provider value={value}>
      {children}
      <TransitionGroup component={null}>
        {modalStack.map(({ id, component }, index) => {
          const depth = modalStack.length - 1 - index;

          // Only the top modal is interactive.
          const isTopmost = depth === 0;

          // Define the styles for the stacking effect.
          const style = {
            transform: `scale(${isTopmost ? 1 : 1 - depth * 0.05})`,
            // Move modals underneath slightly up so they stack nicely
            top: isTopmost ? 'auto' : `-${depth * 1}rem`,
            // Only the top modal can be interacted with.
            pointerEvents: isTopmost ? 'auto' : 'none',
            transition: 'transform 0.3s ease-in-out, top 0.3s ease-in-out',
          };

          return (
            <CSSTransition key={id} timeout={300} classNames={isTopmost ? 'modal-backdrop' : ''} unmountOnExit>
              {/* The Backdrop: Handles "click outside" ONLY for the topmost modal */}
              <div
                className="fixed inset-0 bg-black bg-opacity-60 z-[60] p-4 flex items-center justify-center"
                style={{ zIndex: 60 + index }}
                onMouseDown={isTopmost ? hideModal : undefined}
              >
                {/* We inject props directly onto the component passed to showModal */}
                {cloneElement(component, {
                  style: { ...component.props.style, ...style },
                  isTopmost,
                  onMouseDown: (e) => {
                    e.stopPropagation();
                    if (component.props.onMouseDown) {
                      component.props.onMouseDown(e);
                    }
                  },
                })}
              </div>
            </CSSTransition>
          );
        })}
      </TransitionGroup>
    </ModalContext.Provider>
  );
};
